import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { TextArea } from "@/components/Textarea";
import { Label } from "@/components/Label";
import {
  useCreateProductMutation,
  useUpdateProductMutation,
} from "@/redux/services/menu/menu.api";
import { Product } from "@/redux/services/menu/menu.type";
import { useUploadMedia } from "@/lib/useUploadMedia";
import { convertPriceFromCent, convertPriceToCent, getImageUrl } from "@/lib/utils";
import { useAppSelector } from "@/redux/features/hooks";
import toast from "react-hot-toast";
import { logger } from "@/services/Logger";
import { toastApiError } from "@/lib/apiErrors";

interface ProductFormDrawerProps {
  product?: Product | null;
  isOpen: boolean;
  onClose: () => void;
  /** Fires only after a successful create/edit (not on cancel). */
  onSaved?: () => void;
}

/**
 * Create or edit a dish's identity — the fields shared by every menu the dish
 * is placed on. Per-menu price and availability stay on the placement, edited
 * from inside a menu.
 */
const ProductFormDrawer: React.FC<ProductFormDrawerProps> = ({
  product,
  isOpen,
  onClose,
  onSaved,
}) => {
  const { currentRestaurant } = useAppSelector((s) => s.user);
  const { uploadMedia } = useUploadMedia();
  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();

  const isEditing = !!product?.id;
  const isLoading = isCreating || isUpdating;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [basePrice, setBasePrice] = useState(0);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTitle(product?.title ?? "");
    setDescription(product?.description ?? "");
    setBasePrice(product ? convertPriceFromCent(product.basePrice) : 0);
    setImageFile(null);
    setImagePreview(product?.image?.url ? getImageUrl(product.image.url) : null);
  }, [product, isOpen]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Le nom du plat est requis");
      return;
    }

    try {
      let imagePath: string | undefined;
      if (imageFile) {
        const uploaded = await uploadMedia(imageFile);
        if (uploaded) {
          imagePath = `/api/media_objects/${uploaded.id}`;
        }
      }

      const data = {
        title,
        description,
        basePrice: convertPriceToCent(basePrice),
        ...(imagePath ? { image: imagePath } : {}),
      };

      if (isEditing && product) {
        await updateProduct({ id: product.id, data }).unwrap();
      } else {
        if (!currentRestaurant?.id) {
          toast.error("Aucun restaurant sélectionné");
          return;
        }
        await createProduct({
          restaurantId: currentRestaurant.id,
          data,
        }).unwrap();
      }

      toast.success(`Plat ${isEditing ? "mis à jour" : "créé"} avec succès`);
      onSaved?.();
      onClose();
    } catch (error: any) {
      logger.error("ProductFormDrawer", "Error saving product:", error);
      toastApiError(error);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0"
      >
        <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0">
          <header className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
            <SheetTitle className="text-base">
              {isEditing ? "Modifier le plat" : "Créer un plat"}
            </SheetTitle>
            <SheetDescription hidden>
              Formulaire de création ou modification d'un plat du catalogue.
            </SheetDescription>
          </header>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
            <div>
              <Label label="Image" htmlFor="product-image" />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Aperçu"
                  className="w-20 h-20 object-cover rounded mt-1 mb-2"
                />
              )}
              <input
                type="file"
                id="product-image"
                className="block w-full text-sm border bg-white rounded-lg py-2 px-2 mt-1"
                onChange={handleImageChange}
                accept="image/*"
              />
            </div>

            <Input
              label="Nom"
              type="text"
              id="product-title"
              placeholder="Nom du plat"
              required
              className="h-10 py-2 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <TextArea
              label="Description"
              placeholder="Description du plat"
              id="product-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <div>
              <Input
                label="Prix de base"
                type="number"
                id="product-basePrice"
                placeholder="Prix"
                className="h-10 py-2 text-sm"
                value={basePrice}
                onChange={(e) => setBasePrice(parseFloat(e.target.value) || 0)}
                min={0}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Prix par défaut du plat. Chaque menu peut le remplacer par un
                prix qui lui est propre.
              </p>
            </div>
          </div>

          <footer className="flex-shrink-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button type="submit" isLoading={isLoading}>
              {isEditing ? "Enregistrer" : "Créer"}
            </Button>
          </footer>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default ProductFormDrawer;
